import AirDatepicker from 'air-datepicker';
import localeRo from 'air-datepicker/locale/ro';
import 'air-datepicker/air-datepicker.css';

import Component from '../../components/component/component';
import Datepicker from '../../components/forms/datepicker/datepicker';
import Range from '../../components/forms/range/range';
import Helpers from '../../components/helpers/helpers';
import Icon from '../../components/icon/icon';
import Modal from '../../components/modal/modal';
import Ajax from '../ajax/ajax';

import './calculator.scss';

export enum CalculatorTabsAnimationType {
    fadeAndSlide,
}

export enum CalculatorTabsAnimationDirection {
    in,
    out,
}

interface ICalculatorSettings {
    initClass?: string;
    openClass?: string;
    currentClass?: string;
    controlClass?: string;
    contentClass?: string;
    applyClass?: string;
    animationSlideTime?: number;
    animationFadeTime?: number;
    animationSlideEase?: string;
    animationFadeEase?: string;
    animationType?: CalculatorTabsAnimationType;
}

interface ICalculatorInstallmentValues {
    expectedTotal: number;
    expectedDate: number;
    principal: number;
    interest: number;
    admissionFee: number;
    accessFee: number;
    adminFee: number;
}

interface ICalculatorValues {
    amount: number;
    period: number;
    monthlyPayment: number;
    totalCost: number;
    apr: number;
    interest?: number;
    interestPerc?: number;
    xirr: number;
    accessFee: number;
    amountWithoutAccessFee: number;
    installments: ICalculatorInstallmentValues[];
    commission: number;
    adminFee: number;
    adminFeeTotal: number;
    interestTotal: number;
    commissionFeePerc: number;
    adminFeePerc: number;
    loanProductId: number;
    currencyData: {
        primaryCurrency: string;
        secondaryCurrency: string;
        primaryToSecondaryRate: number;
    };
}

interface ICalculatorSubmit {
    loan: {
        amount: number,
        productId: number,
        currency: string
    }
}

/* Ajax responses */

interface ICalculatorSubmitAjaxResponse {
    success: boolean;
    customerId: string;
    errors?: string[];
    errorStyles?: string;
    data?: ICalculatorSubmit;
}

interface ICalculatorValuesAjaxResponse {
    success: boolean;
    amount: boolean;
    errors?: string[];
    data?: ICalculatorValues;
}

const LOADING_CLASS: string = 'is-loading';

export default class Calculator extends Component {
    static initSelector: string = '.calculator';

    public range: JQuery;
    public form: JQuery;
    public errors: JQuery;
    public submit: JQuery;
    public isConversionActive: boolean;
    public currenyInTable: string;
    public currencyRate: number;
    public useAprForInterest: boolean;

    private settings: ICalculatorSettings;

    private onOpenComplete: () => void;
    private onCloseComplete: () => void;

    private xhr: JQueryXHR;
    private valuesXhr: JQueryXHR;

    private startedTracked: boolean = false;
    private formFilledTracked: boolean = false;

    constructor(target: HTMLElement) {
        super(target);

        this.settings = $.extend({
            animationFadeEase: 'swing',
            animationFadeTime: 300,
            animationSlideEase: 'swing',
            animationSlideTime: 300,
            animationType: CalculatorTabsAnimationType.fadeAndSlide,
            applyClass: 'js-apply',
            contentClass: 'js-content',
            controlClass: 'js-control',
            currentClass: 'is-current',
            initClass: 'calculator',
            openClass: 'is-open',
        }, this.element.data());

        this.range = this.element.find('.calculator__range');
        this.form = this.element.find('.calculator__form');
        this.errors = this.form.find('.calculator__form-errors');
        this.submit = this.form.find('.calculator__form-button');
        this.isConversionActive = this.element.data('isConversionActive') === true;
        this.currenyInTable = this.element.data('currencyInTable');
        this.currencyRate = 1.95583; // BGN to EUR conversion rate
        this.useAprForInterest = this.element.data('useAprForInterest') === true;

        this.setup();
        this.calculate();
    }

    setup(): void {
        this.element.find('.' + this.settings.controlClass).on('click', this.handleControlClick.bind(this));
        this.element.find('.' + this.settings.applyClass).on('click', this.handleApplyClick.bind(this));

        this.range.each((index: number, element: HTMLElement) => {
            const rangeInstance: InstanceType<typeof Range> = Range.create<typeof Range>(element);

            rangeInstance.input.on('change', this.calculate.bind(this));
            rangeInstance.sliderContainer.on('change', this.calculate.bind(this));
        });

        this.element.on('submit', this.handleSubmit.bind(this));

        this.element.find('#check-all-of-the-above').on('click', this.handleCheckAllOfTheAboveClick.bind(this));

        this.enhanceSafariDateInputs();
        this.bindDateFieldClickToOpenPicker();
    }

    enhanceSafariDateInputs(): void {
        const ua: string = navigator.userAgent;
        const isSafari: boolean = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
        const isMobile: boolean =
            /iPad|iPhone|iPod|Mobile/i.test(ua)
            || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);

        if (!isSafari || isMobile) {
            return;
        }

        this.form.find('input[type="date"]').each((_: number, el: HTMLElement) => {
            const input: HTMLInputElement = el as HTMLInputElement;
            const originalName: string = input.name;
            const hidden: HTMLInputElement = document.createElement('input');

            hidden.type = 'hidden';
            hidden.name = originalName;
            input.removeAttribute('name');
            input.insertAdjacentElement('afterend', hidden);

            input.type = 'text';
            input.readOnly = true;
            input.autocomplete = 'off';
            input.placeholder = 'dd.mm.yyyy';

            const dp: AirDatepicker = new AirDatepicker(input, {
                altField: hidden,
                altFieldDateFormat: 'yyyy-MM-dd',
                dateFormat: 'dd.MM.yyyy',
                locale: Datepicker.locale || localeRo,
                navTitles: { days: 'MMM yyyy', months: 'yyyy', years: 'yyyy - yyyy' },
                onSelect: () => input.dispatchEvent(new Event('change', { bubbles: true })),
                position: 'bottom left',
            });

            const closeOnScroll: () => void = (): void => dp.hide();

            window.addEventListener('scroll', closeOnScroll, { passive: true });

            const scrollAncestor: HTMLElement | null = this.findScrollableAncestor(input);

            if (scrollAncestor) {
                scrollAncestor.addEventListener('scroll', closeOnScroll, { passive: true });
            }
        });
    }

    bindDateFieldClickToOpenPicker(): void {
        this.form.find('.calculator__form-field').has('input[type="date"]').on('click', (event: JQuery.TriggeredEvent) => {
            if (!event.originalEvent || (event.originalEvent instanceof MouseEvent && !event.originalEvent.isTrusted)) {
                return;
            }

            const input: HTMLInputElement | undefined = $(event.currentTarget).find('input[type="date"]').get(0) as HTMLInputElement | undefined;

            if (!input || input.type !== 'date' || typeof input.showPicker !== 'function') {
                return;
            }

            try {
                input.showPicker();
            } catch (_) {
                // showPicker requires a user gesture in some browsers; nothing to do if it fails
            }
        });
    }

    /* TABS */

    closeAll(current: JQuery): void {
        const open: JQuery = this.element.find('.' + this.settings.contentClass);

        open.each((index: number, element: HTMLElement): void => {
            const $element: JQuery = $(element);

            if ($element.length && !$element.is(current)) {
                this.animate(CalculatorTabsAnimationDirection.out, $element, current, (): void => {
                    $element.removeClass(this.settings.openClass);
                });
            }
        });

        this.element.find('.' + this.settings.controlClass).removeClass(this.settings.currentClass);
    }

    animate(type: CalculatorTabsAnimationDirection, tab: JQuery, current: JQuery = null, onComplete: () => void = null): void {
        if (tab.length) {
            const triggerOnComplete: () => void = (): void => {
                tab.removeAttr('style');
                if (onComplete) {
                    onComplete();
                }
                switch (type) {
                    case CalculatorTabsAnimationDirection.in:
                        if (this.onOpenComplete) {
                            this.onOpenComplete();
                        }
                        this.onOpenComplete = null;
                        break;
                    case CalculatorTabsAnimationDirection.out:
                        if (this.onCloseComplete) {
                            this.onCloseComplete();
                        }
                        this.onCloseComplete = null;
                        break;
                }
            };

            if (tab.length) {
                if (tab.get(0).offsetHeight === 0) {
                    tab.removeAttr('style');
                }
                const contentHeight: string = tab.get(0).offsetHeight + 'px';
                let newContentHeight: string = '0px';

                if (current) {
                    const isOpen: boolean = current.hasClass(this.settings.openClass);

                    if (!isOpen) {
                        current.addClass(this.settings.openClass);
                    }
                    newContentHeight = current.get(0).offsetHeight + 'px';
                    if (!isOpen) {
                        current.removeClass(this.settings.openClass);
                    }
                }

                const firstTab: JQuery = tab.length ? $(tab.get(0)) : tab;

                switch (this.settings.animationType) {
                    case CalculatorTabsAnimationType.fadeAndSlide:
                        switch (type) {
                            case CalculatorTabsAnimationDirection.in:
                                firstTab.stop().css({
                                    height: contentHeight,
                                    opacity: 0,
                                }).animate({
                                    height: contentHeight,
                                    opacity: 1,
                                }, this.settings.animationFadeTime, this.settings.animationFadeEase, triggerOnComplete);
                                break;
                            case CalculatorTabsAnimationDirection.out:
                                firstTab.stop().animate({
                                    height: newContentHeight,
                                    opacity: 0,
                                }, this.settings.animationSlideTime, this.settings.animationSlideEase, triggerOnComplete);
                                break;
                        }
                        break;
                }
            }
        }
    }

    /* EVENTS */

    handleControlClick(event: JQuery.TriggeredEvent): void {
        const id: string = $(event.currentTarget).data('id');
        const parent: JQuery = this.element;
        const tab: JQuery = parent.find('.calculator__content[id="' + id + '"]');
        const control: JQuery = parent.find('[data-id="' + id + '"].' + this.settings.controlClass);

        const hasActiveControlClass: boolean = control.hasClass(this.settings.currentClass);

        if (tab.length && !tab.hasClass(this.settings.openClass) && !hasActiveControlClass) {
            // hide form
            this.element.removeClass('has-open-form');

            this.onCloseComplete = (): void => {
                if (tab.length) {
                    tab.addClass(this.settings.openClass);
                    if (!Helpers.isOnScreen($(tab.get(0)))) {
                        Helpers.scrollToTarget(tab);
                    }
                }

                this.animate(CalculatorTabsAnimationDirection.in, tab, tab);

                // calculate values
                this.calculate();
            };

            if (parent.length) {
                this.closeAll(tab);
            } else {
                this.onCloseComplete();
            }

            if (parent.length) {
                parent.find('[data-id="' + tab.attr('id') + '"].' + this.settings.controlClass).addClass(this.settings.currentClass);
            }
        }
    }

    handleApplyClick(event: JQuery.TriggeredEvent): void {
        const target: HTMLElement | null = (event.target as HTMLElement).closest('a');

        if (target instanceof HTMLAnchorElement && target.href) {
            return;
        }

        event.preventDefault();

        if (!this.startedTracked) {
            this.startedTracked = true;
            Helpers.pushDataLayerEvent('application_started');
        }

        this.element.addClass('has-open-form');

        const container: JQuery<HTMLElement> = this.element;
        const containerOffset: JQuery.Coordinates = container.offset();
        const form: JQuery<HTMLElement> = this.form;
        const formOffset: JQuery.Coordinates = form.offset();
        const scrollPosition: number = formOffset.top - containerOffset.top;

        container.scrollTop(scrollPosition);

        this.form.find('input').first().trigger('focus');
    }

    handleCheckAllOfTheAboveClick(): void {
        if (this.element.find('#check-all-of-the-above').is(':checked')) {
            this.element.find('.use-for-check-all-of-the-above input:checkbox').each((index: number, element: HTMLElement) => {
                $(element).prop('checked', true);
            });
        } else {
            this.element.find('.use-for-check-all-of-the-above input:checkbox').each((index: number, element: HTMLElement) => {
                $(element).prop('checked', false);
            });
        }
    }

    handleSubmit(event: JQuery.TriggeredEvent): void {
        event.preventDefault();

        this.disableSubmit();
        this.hideFormErrors();

        // validate form
        if (!this.validate()) {
            this.enableSubmit();
            this.scrollToFirstInvalidField();

            return;
        }

        if (!this.formFilledTracked) {
            this.formFilledTracked = true;
            Helpers.pushDataLayerEvent('application_form_filled');
        }

        // get form data
        const formData: FormData = new FormData(this.element.get(0) as HTMLFormElement);

        this.element.find('input:checkbox:not(:checked)').each((index: number, element: HTMLElement) => {
            const check: JQuery = $(element);

            formData.append(check.attr('name'), 'false');
        });

        formData.append('action', 'calculator-submit');

        if (this.xhr) {
            this.xhr.abort();
        }

        this.setSubmitLoading();

        this.xhr = Ajax.post(formData, {
            contentType: false,
        });

        const currentUrl: string = window.location.href;

        this.xhr.then((response: ICalculatorSubmitAjaxResponse): void => {
            if (response.success) {
                this.clearSubmitLoading();

                /* eslint-disable @typescript-eslint/naming-convention */
                Helpers.pushDataLayerEvent('application_successful', {
                    ecommerce: {
                        transaction_id: response.customerId,
                        value: response.data.loan.amount,
                        currency: response.data.loan.currency,
                        items: [{
                            item_id: response.data.loan.productId,
                            item_name: currentUrl,
                        }],
                    },
                });
                /* eslint-enable @typescript-eslint/naming-convention */

                $('html, body').animate({
                    scrollTop: 0,
                }, 300, 'swing', (): void => {
                    // after scroll top get modal and open it
                    const modal: Modal = new Modal(this.element.find('.calculator__modal').get(0), event.currentTarget, true);

                    modal.open(event.currentTarget);
                });
            } else {
                this.clearSubmitLoading();
                this.enableSubmit();

                if (response.errors) {
                    $('#calculator-error-styles').html(response.errorStyles);

                    response.errors.forEach((message: string) => {
                        if (!message?.trim()) {
                            return;
                        }

                        this.errors.append(
                            `<div class="alert alert--error calculator__form-error">${ Icon.render('error-outline', '', 'alert__icon') }<div class="alert__content text">${ message }</div></div>`,
                        );
                    });

                    this.showFormErrors();
                    this.scrollToFirstServerErrorField(response.errorStyles);
                }
            }
        });
    }

    disableSubmit(): void {
        this.submit.prop('disabled', true);
    }

    enableSubmit(): void {
        this.submit.prop('disabled', false);
    }

    setSubmitLoading(): void {
        this.submit.addClass('is-loading');
    }

    clearSubmitLoading(): void {
        this.submit.removeClass('is-loading');
    }

    showFormErrors(): void {
        this.form.addClass('has-errors');
    }

    hideFormErrors(): void {
        this.form.removeClass('has-errors');
        this.errors.empty();
        this.form.find('.calculator__form-field-error').remove();
    }

    /* HELPERS */

    getLocalizedErrorMessage(kind: 'required' | 'pin' | 'gsm' | 'date' | 'select'): string {
        const fallback: Record<string, string> = {
            date: 'Invalid birthdate',
            gsm: 'GSM Error, please check the GSM number entered',
            pin: 'PIN Error, please check the PIN number entered',
            required: 'Please fill all required fields',
            select: 'Please select an option',
        };
        const sourceIds: Record<string, string> = {
            date: '#calculator__form-date-error',
            gsm: '#calculator__form-gsm-pattern-error',
            pin: '#calculator__form-pin-pattern-error',
            required: '#calculator__form-required-error',
            select: '#calculator__form-required-error',
        };
        const text: string = $(sourceIds[kind]).text().trim();

        return text || fallback[kind];
    }

    setFieldError(field: JQuery, message: string): void {
        if (!field.length) {
            return;
        }

        // Only the first failure for a given field is shown.
        if (field.find('> .calculator__form-field-error').length) {
            return;
        }

        field.append(`<p class="calculator__form-field-error">${ message }</p>`);
    }

    scrollToField(field: JQuery, focus: boolean = true): void {
        if (!field.length) {
            return;
        }

        const fieldEl: HTMLElement = field.get(0);
        const scrollAncestor: HTMLElement | null = this.findScrollableAncestor(fieldEl);
        const containerEl: HTMLElement = scrollAncestor || (document.scrollingElement as HTMLElement) || document.documentElement;
        const fieldRect: DOMRect = fieldEl.getBoundingClientRect();
        const delta: number = (fieldRect.top + fieldRect.height / 2) - (window.innerHeight / 2);

        containerEl.scrollBy({ behavior: 'smooth', top: delta });

        if (focus) {
            field.find('input, select, textarea').filter(':visible').first().trigger('focus');
        }
    }

    scrollToFirstInvalidField(): void {
        this.scrollToField(this.form.find('.calculator__form-field.is-invalid').first());
    }

    scrollToFirstServerErrorField(errorStyles: string): void {
        if (!errorStyles) {
            return;
        }

        const selectorRe: RegExp = /input\[name=["'][^"']+["']\]|#[\w-]+/g;
        const matches: HTMLElement[] = [];
        let m: RegExpExecArray | null;

        while ((m = selectorRe.exec(errorStyles)) !== null) {
            this.form.find(m[0]).each((_: number, el: HTMLElement) => {
                matches.push(el);
            });
        }

        if (!matches.length) {
            return;
        }

        matches.sort((a: HTMLElement, b: HTMLElement): number => {
            const rel: number = a.compareDocumentPosition(b);

            if (rel & Node.DOCUMENT_POSITION_FOLLOWING) {
                return -1;
            }

            if (rel & Node.DOCUMENT_POSITION_PRECEDING) {
                return 1;
            }

            return 0;
        });

        this.scrollToField($(matches[0]).closest('.calculator__form-field'), false);
    }

    findScrollableAncestor(node: HTMLElement): HTMLElement | null {
        let el: HTMLElement | null = node.parentElement;

        while (el && el !== document.body) {
            const overflowY: string = window.getComputedStyle(el).overflowY;

            if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
                return el;
            }

            el = el.parentElement;
        }

        return null;
    }

    validate(): boolean {
        // remove all invalid markers from the previous attempt
        this.form.find('.calculator__form-field').removeClass('is-invalid');
        this.form.find('.calculator__form-field-error').remove();

        // find all required inputs
        this.form.find('input, select').filter('[required]').each((index: number, element: HTMLElement) => {
            const input: JQuery = $(element);
            const field: JQuery = input.parents('.calculator__form-field');

            switch (input.attr('type')) {
                case 'text':
                    if (!input.val()) {
                        field.addClass('is-invalid');
                        this.setFieldError(field, this.getLocalizedErrorMessage('required'));
                    } else {
                        // Test regex patterns
                        const regex: RegExp = new RegExp(input.attr('pattern'));

                        if (!regex.test(input.val().toString())) {
                            field.addClass('is-invalid');

                            if (input.attr('name') === 'customer_pin') {
                                this.setFieldError(field, this.getLocalizedErrorMessage('pin'));
                            } else if (input.attr('name') === 'customer_gsm') {
                                this.setFieldError(field, this.getLocalizedErrorMessage('gsm'));
                            } else {
                                this.setFieldError(field, this.getLocalizedErrorMessage('required'));
                            }
                        }
                    }

                    break;
                case 'date':
                case 'textarea':
                    if (!input.val()) {
                        field.addClass('is-invalid');
                        this.setFieldError(field, this.getLocalizedErrorMessage('required'));
                    }
                    break;

                case 'checkbox':
                case 'radio':
                    if (!input.is(':checked')) {
                        field.addClass('is-invalid');
                        this.setFieldError(field, this.getLocalizedErrorMessage('required'));
                    }
                    break;
            }
            if (input.get(0).tagName === 'SELECT') {
                if (!input.val() || input.val() === 'placeholder') {
                    field.addClass('is-invalid');
                    this.setFieldError(field, this.getLocalizedErrorMessage('select'));
                }
            }
        });

        // validate date
        this.form.find('input').filter('[type="date"]').each((index: number, element: HTMLElement) => {
            const input: JQuery = $(element);
            const field: JQuery = input.parents('.calculator__form-field');
            const dateTest: Date = new Date(input.val().toString());

            if (input.val()) {
                if (dateTest.toString() === 'Invalid Date') {
                    field.addClass('is-invalid');
                    this.setFieldError(field, this.getLocalizedErrorMessage('date'));
                } else {
                    const year: number = dateTest.getFullYear();

                    if (year > 9999) {
                        field.addClass('is-invalid');
                        this.setFieldError(field, this.getLocalizedErrorMessage('date'));
                    }
                }
            }
        });

        return !this.form.find('.calculator__form-field.is-invalid').length;
    }

    /* CALCULATIONS */

    getInterestPerc(values: ICalculatorValues): number {
        return this.useAprForInterest && typeof values.apr !== 'undefined' ? Math.round(values.apr) : values.interestPerc;
    }

    calculate(): void {
        const type: string = String(this.element.find('.calculator__tab-input:checked').val());
        const amount: number = Number(this.element.find('[name="' + type + '[loan-amount]"]').val());

        this.setLoadingState();
        switch (type) {
            case 'standard': {
                this.updateStandard(amount);
                break;
            }
            case 'popular': {
                this.updatePopular(amount);
                break;
            }
        }
    }

    updateStandard(amount: number): void {
        const period: number = Number(this.element.find('[name="standard[loan-period]"]').val());

        this.setLoadingState();

        this.values(amount, period).then((response: ICalculatorValuesAjaxResponse): void => {
            if (response.success && response.data) {
                const values: ICalculatorValues = response.data;

                const primaryToSecondaryRate: number = values.currencyData.primaryToSecondaryRate;

                const showAdmission: boolean = this.element.find('[data-id="standard"]').attr('data-show-admission') === '1' ? true : false;
                const showAccess: boolean = this.element.find('[data-id="standard"]').attr('data-show-access') === '1' ? true : false;

                const conclusionTableExpectedDateLabel: string = this.element.find('[data-id="standard"]').attr('data-conclusion_table_expected_date_label');
                const conclusionTablePrincipalLabel: string = this.element.find('[data-id="standard"]').attr('data-conclusion_table_principal_label');
                const conclusionTableInterestLabel: string = this.element.find('[data-id="standard"]').attr('data-conclusion_table_interest_label');
                const conclusionTableAdminLabel: string = this.element.find('[data-id="standard"]').attr('data-conclusion_table_admin_label');
                const conclusionTableAccessFeeLabel: string = this.element.find('[data-id="standard"]').attr('data-conclusion_table_access_fee_label');
                const conclusionTableAdmissionFeeLabel: string = this.element.find('[data-id="standard"]').attr('data-conclusion_table_admission_fee_label');
                const conclusionTableExpectedTotalLabel: string = this.element.find('[data-id="standard"]').attr('data-conclusion_table_expected_total_label');

                this.element.find('.js-conclusion-monthly').text(String(values.monthlyPayment));
                this.element.find('.js-conclusion-monthly-in-secondary').text(String((values.monthlyPayment / primaryToSecondaryRate).toFixed(2)));
                this.element.find('.js-conclusion-commission').text(String(values.commission));
                this.element.find('.js-conclusion-admin_fee').text(String(values.adminFee));
                this.element.find('.js-conclusion-admin_fee_total').text(String(values.adminFeeTotal));
                this.element.find('.js-conclusion-interest_total').text(String(values.interestTotal));
                this.element.find('.js-conclusion-total_cost').text(String(values.totalCost.toFixed(2)));
                this.element.find('.js-package-total_cost-in-eur').html(`(<b>${(values.totalCost / primaryToSecondaryRate).toFixed(2)}</b> ${values.currencyData.secondaryCurrency})`);
                this.element.find('.js-conclusion-xirr').text(String(values.xirr));
                this.element.find('.js-conclusion-access_fee').text(String(values.accessFee));
                this.element.find('.js-conclusion-amount_without_access_fee').text(String(values.amountWithoutAccessFee));
                this.element.find('.js-conclusion-commission_fee_perc').text(String(values.commissionFeePerc));
                this.element.find('.js-conclusion-admin_fee_perc').text(String(values.adminFeePerc));
                this.element.find('.js-conclusion-interest_perc').text(String(this.getInterestPerc(values)));
                this.element.find('.js-conclusion-apr').text(String(values.apr));
                this.element.find('#calculator-product-id').val(String(values.loanProductId));

                const accessFee: JQuery = this.element.find('.js-conclusion-accessfee');
                const cashByHand: JQuery = this.element.find('.js-conclusion-cashbyhand');

                if (typeof values.accessFee !== 'undefined') {
                    accessFee.text(String(values.accessFee));
                }

                if (typeof values.amountWithoutAccessFee !== 'undefined') {
                    cashByHand.text(String(values.amountWithoutAccessFee));
                }

                const tbl: HTMLTableElement = <HTMLTableElement> document.createElement('table');

                tbl.style.width = '100%';
                tbl.classList.add('calculator__installments');

                const tbdy: HTMLTableSectionElement = document.createElement('tbody');

                // Titles

                const titlesTr: HTMLTableRowElement = document.createElement('tr');

                const noTh: HTMLTableCellElement = document.createElement('th');

                noTh.appendChild(document.createTextNode(''));
                titlesTr.appendChild(noTh);

                const dateTh: HTMLTableCellElement = document.createElement('th');

                dateTh.appendChild(document.createTextNode(conclusionTableExpectedDateLabel));
                titlesTr.appendChild(dateTh);

                const principalTh: HTMLTableCellElement = document.createElement('th');

                if (conclusionTablePrincipalLabel) {
                    principalTh.appendChild(document.createTextNode(conclusionTablePrincipalLabel));
                    titlesTr.appendChild(principalTh);
                }

                const interestTh: HTMLTableCellElement = document.createElement('th');

                if (conclusionTableInterestLabel) {
                    interestTh.appendChild(document.createTextNode(conclusionTableInterestLabel));
                    titlesTr.appendChild(interestTh);
                }

                const adminTh: HTMLTableCellElement = document.createElement('th');

                if (conclusionTableAdminLabel) {
                    adminTh.appendChild(document.createTextNode(conclusionTableAdminLabel));
                    titlesTr.appendChild(adminTh);
                }

                if (showAccess) {
                    const accessTh: HTMLTableCellElement = document.createElement('th');

                    if (conclusionTableAccessFeeLabel) {
                        accessTh.appendChild(document.createTextNode(conclusionTableAccessFeeLabel));
                        titlesTr.appendChild(accessTh);
                    }
                }

                if (showAdmission) {
                    const admissionTh: HTMLTableCellElement = document.createElement('th');

                    if (conclusionTableAdmissionFeeLabel) {
                        admissionTh.appendChild(document.createTextNode(conclusionTableAdmissionFeeLabel));
                        titlesTr.appendChild(admissionTh);
                    }
                }

                const totalTh: HTMLTableCellElement = document.createElement('th');

                if (conclusionTableExpectedTotalLabel) {
                    totalTh.appendChild(document.createTextNode(conclusionTableExpectedTotalLabel));
                    titlesTr.appendChild(totalTh);
                }

                tbdy.appendChild(titlesTr);

                let i: number = 0;

                for (const key in values.installments) {
                    if (Object.prototype.hasOwnProperty.call(values.installments, key)) {
                        i++;

                        if (i <= 1) {
                            continue;
                        }

                        const installment: ICalculatorInstallmentValues = values.installments[key];

                        const tr: HTMLTableRowElement = document.createElement('tr');

                        // Number

                        const expectedDateNo: HTMLTableCellElement = document.createElement('td');

                        expectedDateNo.appendChild(document.createTextNode((i-1).toString()));
                        tr.appendChild(expectedDateNo);

                        // Expected date

                        const expectedDateTd: HTMLTableCellElement = document.createElement('td');

                        expectedDateTd.appendChild(document.createTextNode(installment.expectedDate.toString()));
                        tr.appendChild(expectedDateTd);

                        // Principal

                        if (conclusionTablePrincipalLabel && installment.principal) {
                            const principalTd: HTMLTableCellElement = document.createElement('td');
                            const principalTdText: string = this.isConversionActive ? `${installment.principal.toString()} ${values.currencyData.primaryCurrency} (${(installment.principal / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.principal.toString();

                            principalTd.appendChild(document.createTextNode(principalTdText));
                            tr.appendChild(principalTd);
                        }

                        // Interest

                        if (conclusionTableInterestLabel && installment.interest) {
                            const interestTd: HTMLTableCellElement = document.createElement('td');
                            const interestTdText: string = this.isConversionActive ? `${installment.interest.toString()} ${values.currencyData.primaryCurrency} (${(installment.interest / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.interest.toString();

                            interestTd.appendChild(document.createTextNode(interestTdText));
                            tr.appendChild(interestTd);
                        }

                        // Admin

                        if (conclusionTableAdminLabel && installment.adminFee) {
                            const adminTd: HTMLTableCellElement = document.createElement('td');
                            const adminTdText: string = this.isConversionActive ? `${installment.adminFee.toString()} ${values.currencyData.primaryCurrency} (${(installment.adminFee / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.adminFee.toString();

                            adminTd.appendChild(document.createTextNode(adminTdText));
                            tr.appendChild(adminTd);
                        }

                        // Access fee

                        if (showAccess && conclusionTableAccessFeeLabel && installment.accessFee) {
                            const accessFeeTd: HTMLTableCellElement = document.createElement('td');
                            const accessFeeTdText: string = this.isConversionActive ? `${installment.accessFee.toString()} ${values.currencyData.primaryCurrency} (${(installment.accessFee / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.accessFee.toString();

                            accessFeeTd.appendChild(document.createTextNode(accessFeeTdText));
                            tr.appendChild(accessFeeTd);
                        }

                        // Admission (commission) fee

                        if (showAdmission && conclusionTableAdmissionFeeLabel && installment.admissionFee) {
                            const admissionFeeTd: HTMLTableCellElement = document.createElement('td');
                            const admissionFeeTdText: string = this.isConversionActive ? `${installment.admissionFee.toString()} ${values.currencyData.primaryCurrency} (${(installment.admissionFee / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.admissionFee.toString();

                            admissionFeeTd.appendChild(document.createTextNode(admissionFeeTdText));
                            tr.appendChild(admissionFeeTd);
                        }

                        // Expected total

                        if (conclusionTableExpectedTotalLabel && installment.expectedTotal) {
                            const expectedTotalTd: HTMLTableCellElement = document.createElement('td');
                            const expectedTotalTdText: string = this.isConversionActive ? `${installment.expectedTotal.toString()} ${values.currencyData.primaryCurrency} (${(installment.expectedTotal / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.expectedTotal.toString();

                            expectedTotalTd.appendChild(document.createTextNode(expectedTotalTdText));
                            tr.appendChild(expectedTotalTd);
                        }

                        // Append td-s to tr

                        tbdy.appendChild(tr);
                    }
                }
                tbl.appendChild(tbdy);

                $('#installments-modal .modal__content').html(tbl);

                // hide access fee and cash by hand if access fee is zero
                /*
                if (values.accessFee === 0) {
                    accessFee.parents('.calculator__conclusion-calculation').addClass('h-hidden');
                    cashByHand.parents('.calculator__conclusion-calculation').addClass('h-hidden');
                } else {
                    accessFee.parents('.calculator__conclusion-calculation').removeClass('h-hidden');
                    cashByHand.parents('.calculator__conclusion-calculation').removeClass('h-hidden');
                }
                */
                this.disableLoadingState();
            }
        });
    }

    updatePopular(amount: number): void {
        const packages: JQuery = this.element.find('.calculator__package');

        this.setLoadingState();

        packages.each((index: number, element: HTMLElement) => {
            const item: JQuery = $(element);
            const input: JQuery = item.find('[name="popular[loan-period]"]');

            const period: number = Number(input.val());
            const product: number = Number(input.data('product'));

            const productId: string = item.attr('data-product-id');
            const isLff: string = item.attr('data-is-lff');
            const lffMax: string = item.attr('data-lff-max');

            this.values(amount, period).then((response: ICalculatorValuesAjaxResponse): void => {
                if (response.success && response.data) {
                    this.disableLoadingState();

                    const values: ICalculatorValues = response.data;

                    const showAdmission: boolean = this.element.find('[data-id="popular"]').attr('data-show-admission') === '1' ? true : false;
                    const showAccess: boolean = this.element.find('[data-id="popular"]').attr('data-show-access') === '1' ? true : false;

                    const conclusionTableExpectedDateLabel: string = this.element.find('[data-id="popular"]').attr('data-conclusion_table_expected_date_label');
                    const conclusionTablePrincipalLabel: string = this.element.find('[data-id="popular"]').attr('data-conclusion_table_principal_label');
                    const conclusionTableInterestLabel: string = this.element.find('[data-id="popular"]').attr('data-conclusion_table_interest_label');
                    const conclusionTableAdminLabel: string = this.element.find('[data-id="popular"]').attr('data-conclusion_table_admin_label');
                    const conclusionTableAccessFeeLabel: string = this.element.find('[data-id="popular"]').attr('data-conclusion_table_access_fee_label');
                    const conclusionTableAdmissionFeeLabel: string = this.element.find('[data-id="popular"]').attr('data-conclusion_table_admission_fee_label');
                    const conclusionTableExpectedTotalLabel: string = this.element.find('[data-id="popular"]').attr('data-conclusion_table_expected_total_label');

                    if (isLff === '1') {
                        if (values.amount > Number(lffMax)) {
                            item.css('opacity', '0.5');
                            item.css('pointer-events', 'none');

                            item.next('.calculator-package').find('.calculator-package__input').prop('checked', true);

                            return;
                        } else {
                            item.css('opacity', '1');
                            item.css('pointer-events', 'auto');
                        }

                        const totalWin: number = parseFloat(((values.totalCost + (values.amount / 100 * values.commissionFeePerc)) - values.amount).toFixed(2));
                        const primaryToSecondaryRate: number = values.currencyData.primaryToSecondaryRate;

                        item.find('.js-package-regular-amount').html(String(((values.amount + totalWin) * 0.5).toFixed(2)));
                        item.find('.js-package-regular-amount-in-secondary').html(String((((values.amount + totalWin) * 0.5) / primaryToSecondaryRate).toFixed(2)));
                        item.find('.js-package-total-win').html(String(totalWin));
                        item.find('.js-package-total-win-in-secondary').html(String((totalWin / primaryToSecondaryRate).toFixed(2)));
                        item.find('.js-package-amount').html(String(values.amount / 2));
                        item.find('.js-package-amount-in-secondary').text(`${((values.amount / 2) / primaryToSecondaryRate).toFixed(2)}`);

                    } else {
                        const primaryToSecondaryRate: number = values.currencyData.primaryToSecondaryRate;

                        item.find('.js-package-amount').html(String(values.monthlyPayment));
                        item.find('.js-package-amount-in-secondary').text(`${(values.monthlyPayment / primaryToSecondaryRate).toFixed(2)}`);
                    }

                    if (isLff !== '1') {
                        const primaryToSecondaryRate: number = values.currencyData.primaryToSecondaryRate;

                        item.find('.js-package-total').html(String(values.totalCost));
                        item.find('.js-package-interest').html(String(this.getInterestPerc(values)));
                        item.find('.js-package-apr').html(String(values.apr));
                        item.find('.js-package-xirr').html(String(values.xirr));

                        item.find('.js-package-monthly').text(String(values.monthlyPayment));
                        item.find('.js-package-commission').text(String(values.commission));
                        item.find('.js-package-admin_fee').text(String(values.adminFee));
                        item.find('.js-package-admin_fee_total').text(String(values.adminFeeTotal));
                        item.find('.js-package-interest_total').text(String(values.interestTotal));
                        item.find('.js-package-total_cost').text(String(values.totalCost));
                        item.find('.js-package-total_cost-in-eur').html(`(<b>${(values.totalCost / primaryToSecondaryRate).toFixed(2)}</b> ${values.currencyData.secondaryCurrency})`);
                        item.find('.js-package-xirr').text(String(values.xirr));
                        item.find('.js-package-access_fee').text(String(values.accessFee));
                        item.find('.js-package-amount_without_access_fee').text(String(values.amountWithoutAccessFee));
                        item.find('.js-package-commission_fee_perc').text(String(values.commissionFeePerc));
                        item.find('.js-package-admin_fee_perc').text(String(values.adminFeePerc));
                        item.find('.js-package-interest_perc').text(String(this.getInterestPerc(values)));
                        item.find('.js-package-apr').text(String(values.apr));

                        const accessFee: JQuery = item.find('.js-package-accessfee');
                        const cashByHand: JQuery = item.find('.js-package-cashbyhand');

                        if (typeof values.accessFee !== 'undefined') {
                            accessFee.text(String(values.accessFee));
                        }

                        if (typeof values.amountWithoutAccessFee !== 'undefined') {
                            cashByHand.text(String(values.amountWithoutAccessFee));
                        }
                    }

                    this.element.find('#calculator-product-id').val(String(values.loanProductId));

                    const tbl: HTMLTableElement = <HTMLTableElement> document.createElement('table');

                    tbl.style.width = '100%';
                    tbl.classList.add('calculator__installments');

                    const tbdy: HTMLTableSectionElement = document.createElement('tbody');

                    // Titles

                    const titlesTr: HTMLTableRowElement = document.createElement('tr');

                    const noTh: HTMLTableCellElement = document.createElement('th');

                    noTh.appendChild(document.createTextNode(''));
                    titlesTr.appendChild(noTh);

                    const dateTh: HTMLTableCellElement = document.createElement('th');

                    dateTh.appendChild(document.createTextNode(conclusionTableExpectedDateLabel));
                    titlesTr.appendChild(dateTh);

                    const principalTh: HTMLTableCellElement = document.createElement('th');

                    if (conclusionTablePrincipalLabel) {
                        principalTh.appendChild(document.createTextNode(conclusionTablePrincipalLabel));
                        titlesTr.appendChild(principalTh);
                    }

                    const interestTh: HTMLTableCellElement = document.createElement('th');

                    if (conclusionTableInterestLabel) {
                        interestTh.appendChild(document.createTextNode(conclusionTableInterestLabel));
                        titlesTr.appendChild(interestTh);
                    }

                    const adminTh: HTMLTableCellElement = document.createElement('th');

                    if (conclusionTableAdminLabel) {
                        adminTh.appendChild(document.createTextNode(conclusionTableAdminLabel));
                        titlesTr.appendChild(adminTh);
                    }

                    if (showAccess) {
                        const accessTh: HTMLTableCellElement = document.createElement('th');

                        if (conclusionTableAccessFeeLabel) {
                            accessTh.appendChild(document.createTextNode(conclusionTableAccessFeeLabel));
                            titlesTr.appendChild(accessTh);
                        }
                    }

                    if (showAdmission) {
                        const admissionTh: HTMLTableCellElement = document.createElement('th');

                        if (conclusionTableAdmissionFeeLabel) {
                            admissionTh.appendChild(document.createTextNode(conclusionTableAdmissionFeeLabel));
                            titlesTr.appendChild(admissionTh);
                        }
                    }

                    const totalTh: HTMLTableCellElement = document.createElement('th');

                    if (conclusionTableExpectedTotalLabel) {
                        totalTh.appendChild(document.createTextNode(conclusionTableExpectedTotalLabel));
                        titlesTr.appendChild(totalTh);
                    }

                    tbdy.appendChild(titlesTr);

                    let i: number = 0;

                    for (const key in values.installments) {
                        if (Object.prototype.hasOwnProperty.call(values.installments, key)) {
                            i++;

                            if (i <= 1) {
                                continue;
                            }

                            const installment: ICalculatorInstallmentValues = values.installments[key];
                            const primaryToSecondaryRate: number = values.currencyData.primaryToSecondaryRate;

                            const tr: HTMLTableRowElement = document.createElement('tr');

                            // Number

                            const expectedDateNo: HTMLTableCellElement = document.createElement('td');

                            expectedDateNo.appendChild(document.createTextNode((i-1).toString()));
                            tr.appendChild(expectedDateNo);

                            // Expected date

                            const expectedDateTd: HTMLTableCellElement = document.createElement('td');

                            expectedDateTd.appendChild(document.createTextNode(installment.expectedDate.toString()));
                            tr.appendChild(expectedDateTd);

                            // Principal

                            if (conclusionTablePrincipalLabel && installment.principal) {
                                const principalTd: HTMLTableCellElement = document.createElement('td');

                                if (isLff === '1') {
                                    const principalTdText: string = this.isConversionActive ? `${(values.amount / 2).toString()} ${values.currencyData.primaryCurrency} (${(values.amount / 2 / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : (values.amount / 2).toString();

                                    principalTd.appendChild(document.createTextNode(principalTdText));
                                } else {
                                    const principalTdText: string = this.isConversionActive ? `${installment.principal.toString()} ${values.currencyData.primaryCurrency} (${(installment.principal / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.principal.toString();

                                    principalTd.appendChild(document.createTextNode(principalTdText));
                                }

                                tr.appendChild(principalTd);
                            }

                            // Interest

                            if (conclusionTableInterestLabel) {
                                const interestTd: HTMLTableCellElement = document.createElement('td');

                                if (isLff === '1') {
                                    const interestTdText: string = this.isConversionActive ? `0 ${values.currencyData.primaryCurrency} (0 ${values.currencyData.secondaryCurrency})` : '0';

                                    interestTd.appendChild(document.createTextNode(interestTdText));
                                } else {
                                    const interestTdText: string = this.isConversionActive ? `${installment.interest.toString()} ${values.currencyData.primaryCurrency} (${(installment.interest / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.interest.toString();

                                    interestTd.appendChild(document.createTextNode(interestTdText));
                                }

                                if (installment.interest) {
                                    tr.appendChild(interestTd);
                                }
                            }

                            // Admin

                            if (conclusionTableAdminLabel) {
                                const adminTd: HTMLTableCellElement = document.createElement('td');

                                if (isLff === '1') {
                                    const adminTdText: string = this.isConversionActive ? `0 ${values.currencyData.primaryCurrency} (0 ${values.currencyData.secondaryCurrency})` : '0';

                                    adminTd.appendChild(document.createTextNode(adminTdText));
                                } else {
                                    const adminTdText: string = this.isConversionActive ? `${installment.adminFee.toString()} ${values.currencyData.primaryCurrency} (${(installment.adminFee / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.adminFee.toString();

                                    adminTd.appendChild(document.createTextNode(adminTdText));
                                }

                                if (installment.adminFee) {
                                    tr.appendChild(adminTd);
                                }
                            }

                            // Access fee

                            if (showAccess && conclusionTableAccessFeeLabel) {
                                const accessFeeTd: HTMLTableCellElement = document.createElement('td');

                                if (isLff === '1') {
                                    const accessFeeTdText: string = this.isConversionActive ? `0 ${values.currencyData.primaryCurrency} (0 ${values.currencyData.secondaryCurrency})` : '0';

                                    accessFeeTd.appendChild(document.createTextNode(accessFeeTdText));
                                } else {
                                    const accessFeeTdText: string = this.isConversionActive ? `${installment.accessFee.toString()} ${values.currencyData.primaryCurrency} (${(installment.accessFee / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.accessFee.toString();

                                    accessFeeTd.appendChild(document.createTextNode(accessFeeTdText));
                                }

                                if (installment.accessFee) {
                                    tr.appendChild(accessFeeTd);
                                }
                            }

                            // Admission (commission) fee

                            if (showAdmission && conclusionTableAdmissionFeeLabel) {
                                const admissionFeeTd: HTMLTableCellElement = document.createElement('td');

                                if (isLff === '1') {
                                    const admissionFeeTdText: string = this.isConversionActive ? `0 ${values.currencyData.primaryCurrency} (0 ${values.currencyData.secondaryCurrency})` : '0';

                                    admissionFeeTd.appendChild(document.createTextNode(admissionFeeTdText));
                                } else {
                                    const admissionFeeTdText: string = this.isConversionActive ? `${installment.admissionFee.toString()} ${values.currencyData.primaryCurrency} (${(installment.admissionFee / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.admissionFee.toString();

                                    admissionFeeTd.appendChild(document.createTextNode(admissionFeeTdText));
                                }

                                if (installment.admissionFee) {
                                    tr.appendChild(admissionFeeTd);
                                }
                            }

                            // Expected total

                            const expectedTotalTd: HTMLTableCellElement = document.createElement('td');

                            if (isLff === '1') {
                                const expectedTotalTdText: string = this.isConversionActive ? `${(values.amount / 2).toString()} ${values.currencyData.primaryCurrency} (${(values.amount / 2 / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : (values.amount / 2).toString();

                                expectedTotalTd.appendChild(document.createTextNode(expectedTotalTdText));
                            } else {
                                const expectedTotalTdText: string = this.isConversionActive ? `${installment.expectedTotal.toString()} ${values.currencyData.primaryCurrency} (${(installment.expectedTotal / primaryToSecondaryRate).toFixed(2).toString()} ${values.currencyData.secondaryCurrency})` : installment.expectedTotal.toString();

                                expectedTotalTd.appendChild(document.createTextNode(expectedTotalTdText));
                            }

                            if (installment.expectedTotal) {
                                tr.appendChild(expectedTotalTd);
                            }

                            // Append td-s to tr

                            tbdy.appendChild(tr);
                        }
                    }
                    tbl.appendChild(tbdy);

                    $('#installments-modal-' + productId + ' .modal__content').html(tbl);

                    // hide access fee and cash by hand if access fee is zero
                    /*
                    if (values.accessFee === 0) {
                        accessFee.parents('.package__calculation').addClass('h-hidden');
                        cashByHand.parents('.package__calculation').addClass('h-hidden');
                    } else {
                        accessFee.parents('.package__calculation').removeClass('h-hidden');
                        cashByHand.parents('.package__calculation').removeClass('h-hidden');
                    }
                    */

                    // update product input
                    this.element.find('[name="popular[loan-product]"]').val(product);
                }
            });
        });
    }

    values(amount: number, period: number): JQueryXHR {
        // if (this.valuesXhr) {
        //     this.valuesXhr.abort();
        // }

        this.valuesXhr = Ajax.post({ action: 'calculator-values', amount, period }, { async: true });

        return this.valuesXhr;
    }

    setLoadingState(): void {
        this.element.parent().addClass(LOADING_CLASS);
    }

    disableLoadingState(): void {
        this.element.parent().removeClass(LOADING_CLASS);
    }
}
